import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useEligibilityContext } from '@react/Eligibility/useEligibilityState';
import { useHabitat } from '@react/shared/HabitatContext';
import { useDebounce } from 'use-debounce/lib';
import Button from '../Button';
const SearchForm = ({ initialValue, showMessage = false, showProductMessage = false, }) => {
    const [{ employers, hasSelectedEmployer, selectedEmployer, accessToken }, dispatch,] = useEligibilityContext();
    const { culturecode, errormessage, searchtitle, searchplaceholder, producterrormessage, } = useHabitat();
    const [searchValue, setSearchValue] = useState(initialValue ?? '');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [debouncedSearchValue] = useDebounce(searchValue, 300);
    const triggerSearchTypeEvent = (type) => {
        const event = new CustomEvent('eligibility-search', { detail: type });
        document.dispatchEvent(event);
    };
    // Update value if the initial value changes.
    useEffect(() => {
        setSearchValue(initialValue ?? '');
    }, [initialValue]);
    useEffect(() => {
        getEmployers();
        async function getEmployers() {
            const employers = await getSuggestions(debouncedSearchValue, 10);
            setSuggestions(employers);
        }
    }, [debouncedSearchValue]);
    const autoSuggestInputClassNames = classNames({
        ['ec-search__form-input']: true,
        ['ec-search__form-input--verified']: selectedEmployer !== undefined,
    });
    return (React.createElement("form", { className: "ec-search__form form-input-container", onSubmit: handleSearch },
        React.createElement("label", { className: "ec-search__form-label", htmlFor: "employerSearch" }, searchtitle),
        React.createElement("div", { className: "ec-search__form-field" },
            React.createElement(Autosuggest, { getSuggestionValue: getSuggestionValue, inputProps: {
                    id: 'employerSearch',
                    className: autoSuggestInputClassNames,
                    type: 'text',
                    placeholder: searchplaceholder,
                    value: searchValue ?? '',
                    onChange: (event) => setSearchValue(event.target.value ?? ''),
                    onFocus: () => {
                        document.dispatchEvent(new Event('eligibility-input-focus'));
                    },
                }, shouldRenderSuggestions: shouldRenderSuggestions, renderSuggestion: renderSuggestion, suggestions: suggestions, onSuggestionSelected: (event, data) => onSuggestionSelected(event, data, 'autoSuggest'), onSuggestionsFetchRequested: onSuggestionsFetchRequested, onSuggestionsClearRequested: onSuggestionsClearRequested }),
            React.createElement(Button, { text: "Let's go", disabled: loading })),
        showMessage &&
            !selectedEmployer?.salaryFinanceEmployerDetails &&
            !showProductMessage &&
            initialValue && (React.createElement("p", { className: "ec-search__form-message" }, formatMessage(errormessage, initialValue))),
        showMessage && showProductMessage && initialValue && (React.createElement("p", { className: "ec-search__form-message" }, formatMessage(producterrormessage, initialValue)))));
    async function getSuggestions(value, limit) {
        if (!value?.length) {
            return [];
        }
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;
        if (inputLength === 0) {
            return [];
        }
        let employers = new Array();
        switch (culturecode) {
            case 'uk':
                employers = await fetchEmployersFromSFApi(inputValue, limit);
                break;
            case 'us':
                employers = fetchEmployers(inputValue, limit);
                break;
            default:
                break;
        }
        return employers;
    }
    function getSuggestionValue(suggestion) {
        return suggestion.employerName ?? '';
    }
    async function handleSearch(event) {
        event.preventDefault();
        if (hasSelectedEmployer &&
            searchValue.toLowerCase() === selectedEmployer?.employerName?.toLowerCase()) {
            return;
        }
        const employers = await getSuggestions(searchValue, 10);
        const suggestion = employers?.find(e => employerNameComparison(e, searchValue));
        if (suggestion) {
            await onSuggestionSelected(event, { suggestion }, 'button');
        }
        else {
            dispatch({
                type: 'SET_SELECTED_EMPLOYEE',
                payload: {
                    searchValue: searchValue,
                    employer: undefined,
                    employerNotFound: true,
                },
            });
        }
    }
    async function onSuggestionSelected(event, { suggestion }, type) {
        event.preventDefault();
        triggerSearchTypeEvent(type);
        // Set autosuggest's textbox with employer name till the employer is fetched
        setSearchValue(suggestion.employerName ?? '');
        // If we select the already selected employer then update local state.
        if (suggestion.uniqueIdentifier === selectedEmployer?.uniqueIdentifier)
            return;
        setLoading(true);
        let employer;
        switch (culturecode) {
            case 'uk':
                employer = await fetchEmployerFromSFApi(suggestion.uniqueIdentifier ?? '');
                break;
            case 'us':
                employer = await fetchEmployer(suggestion.uniqueIdentifier ?? '', culturecode);
                break;
            default:
                break;
        }
        if (employer) {
            const event = new CustomEvent('eligibility-result', {
                detail: { type: 'products available' },
            });
            document.dispatchEvent(event);
            // Otherwise update global state.
            dispatch({
                type: 'SET_SELECTED_EMPLOYEE',
                payload: {
                    employer: employer,
                    searchValue: employer?.employerName,
                },
            });
        }
        setLoading(false);
    }
    function onSuggestionsClearRequested() {
        setSuggestions([]);
    }
    function onSuggestionsFetchRequested({ value }) {
        setSearchValue(value);
    }
    function renderSuggestion(suggestion) {
        return React.createElement("div", { dangerouslySetInnerHTML: { __html: highlightSuggestionValue(suggestion) } });
    }
    function shouldRenderSuggestions(value) {
        if (!value || (hasSelectedEmployer && employerNameComparison(selectedEmployer, value))) {
            return false;
        }
        return value.trim().length > 0;
    }
    async function fetchEmployer(uniqueIdentifier, cultureCode) {
        const res = await fetch(`/umbraco/api/employersapi/getemployer?employerName=${encodeURIComponent(uniqueIdentifier)}&culturecode=${cultureCode}`, {
            headers: {
                accept: 'application/json',
            },
        });
        if (!res.ok) {
            return null;
        }
        const response = (await res.json());
        return response;
    }
    function fetchEmployers(text, limit) {
        // Use a Set to remove duplicates
        const set = new Set();
        // First get suggestions that start with the input value.
        employers
            .filter(e => e.employerName?.toLowerCase().startsWith(text.toLowerCase()))
            .forEach(emp => set.add(emp));
        // Then get suggestions that contain the input value.
        employers
            .filter(e => e.employerName?.indexOf(text.toLowerCase()) !== -1)
            .forEach(emp => set.add(emp));
        // Return the top 5 entries.
        return [...set].slice(0, limit);
    }
    async function fetchEmployerFromSFApi(uniqueIdentifier) {
        const res = await fetch(`/umbraco/api/employerssearchapi/getemployer?uniqueIdentifier=${uniqueIdentifier}`, {
            headers: {
                accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (!res.ok) {
            return null;
        }
        const response = (await res.json());
        return response;
    }
    async function fetchEmployersFromSFApi(text, limit) {
        const res = await fetch(`/umbraco/api/employerssearchapi/getemployers?name=${encodeURIComponent(text)}&limit=${limit}`, {
            headers: {
                accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (!res.ok) {
            return [];
        }
        const response = (await res.json());
        return response ?? [];
    }
};
export function highlightSuggestionValue(suggestion) {
    const employerName = suggestion.employerName;
    const highlights = suggestion?.highlight ? suggestion?.highlight.split('-') : [];
    const highlightFrom = highlights.length === 2 ? parseInt(highlights[0]) : 0;
    const highlightTo = highlights.length === 2 ? parseInt(highlights[1]) : 0;
    return `${employerName?.substring(0, highlightFrom)}<b>${employerName?.substring(highlightFrom, highlightTo)}</b>${employerName?.substr(highlightTo)}`;
}
function employerNameComparison(employer, name) {
    if (!employer || !name?.length) {
        return false;
    }
    return (employer?.employerName?.toLowerCase().replace(' ', '') === name?.toLowerCase().replace(' ', ''));
}
function formatMessage(template, searchValue) {
    return template.replace('{searchValue}', searchValue);
}
export default SearchForm;
